import React from "react";
import * as Yup from "yup";
import { useController, useForm } from "react-hook-form";
import { format, isValid } from "date-fns";
import { Box, Button, Grid2, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  FormElement,
  renderFormElement,
  parseOrUndefined,
  toString,
} from "@helpers";
import { Modal } from "@components";
import { CsvCashTransactionRow } from "@bn/api/apiv3";
import { yupResolver } from "@hookform/resolvers/yup";
import { InputFieldWithRevert } from "@features/transactionWizard/components/NumberInputFieldWithRevert";

interface FormFields {
  bank_booking_date?: string;
  counterpart_name?: string;
  counterpart_iban?: string;
  purpose?: string;
  amount?: string;
}

const formFields: FormElement[] = [
  {
    name: "bank_booking_date",
    type: "date",
  },
  {
    name: "counterpart_name",
    testId: "counterpart-name-input-field",
    type: "text",
  },
  {
    name: "counterpart_iban",
    type: "text",
  },
  {
    name: "amount",
    type: "text",
  },
  {
    name: "purpose",
    testId: "purpose-input-field",
    type: "text",
  },
];

type Props = {
  row: CsvCashTransactionRow;
  onClose: () => void;
  isOpen: boolean;
  onSave: (result: CsvCashTransactionRow) => void;
};

export const AccountParseEditModal = ({
  row,
  onClose,
  isOpen,
  onSave,
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isDownSM = useMediaQuery(theme.breakpoints.down("sm"));

  const schema = Yup.object().shape({
    bank_booking_date: Yup.string()
      .required(t("form.errors.required"))
      .nullable()
      .test(
        "test-date",
        t("form.errors.wrongDateFormat"),
        (value: string | null | undefined) =>
          Boolean(value) && isValid(new Date(value as string))
      ),
    counterpart_name: Yup.string()
      .required(t("form.errors.required"))
      .nullable(),
    counterpart_iban: Yup.string().nullable(),
    purpose: Yup.string().nullable(),
    amount: Yup.string().nullable().required(t("form.errors.required")),
  });

  const { control, handleSubmit } = useForm<FormFields>({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      bank_booking_date: row.bank_booking_date?.value,
      counterpart_name: row.counterpart_name?.value,
      counterpart_iban: row.counterpart_iban?.value,
      purpose: row.purpose?.value,
      amount: row.amount?.value,
    },
  });

  const {
    field: amountField,
    fieldState: { error: amountError },
  } = useController({
    control,
    name: "amount",
    defaultValue: row.amount?.value,
  });

  const handleAmountChange = (value: string) => {
    const parsedAmount = parseOrUndefined(value);
    const validWithChar =
      (value.endsWith(",") || value.endsWith(".")) &&
      parseOrUndefined(value.slice(0, -1)) !== undefined;
    if (value.trim() === "" || parsedAmount || validWithChar) {
      amountField.onChange(toString(parsedAmount, value, 2));
    }
  };

  const onSubmit = (data: FormFields) => {
    const newParsingResult: CsvCashTransactionRow = {
      id: row.id,
      bank_booking_date: {
        ...row.bank_booking_date,
        error: undefined,
        value: format(new Date(data?.bank_booking_date ?? ""), "yyyy-MM-dd"),
      },
      counterpart_name: {
        ...row.counterpart_name,
        error: undefined,
        value: data.counterpart_name,
      },
      counterpart_iban: {
        ...row.counterpart_iban,
        error: undefined,
        value: data.counterpart_iban,
      },
      purpose: {
        ...row.purpose,
        error: undefined,
        value: data.purpose,
      },
      amount: {
        ...row.amount,
        error: undefined,
        value: data.amount?.toString(),
      },
    };

    onSave(newParsingResult);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("csvImport.edit")}
      height={isDownSM ? "100%" : "auto"}
      width={isDownSM ? "100%" : "830px"}
    >
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        height="100%"
        justifyContent="space-between"
      >
        <Grid2
          container
          spacing={6}
          sx={{
            overflowY: "auto",
          }}
        >
          {formFields.map((field) =>
            field.name === "amount" ? (
              <Grid2 key={field.name} size={{ xs: isDownSM ? 12 : 6 }}>
                <InputFieldWithRevert
                  fieldLabel={t("csvImport.cashParsingResults.amount")}
                  id="amount"
                  value={amountField.value ?? "0"}
                  handleChange={handleAmountChange}
                  errorMessage={amountError?.message}
                  maximumFractionDigits={2}
                />
              </Grid2>
            ) : (
              <Grid2
                key={field.name}
                size={{ xs: isDownSM || field.name === "purpose" ? 12 : 6 }}
              >
                {renderFormElement(
                  "csvImport.cashParsingResults",
                  field,
                  control,
                  {
                    label: t(`csvImport.cashParsingResults.${field.name}`),
                  }
                )}
              </Grid2>
            )
          )}
        </Grid2>
        <Box
          pt={8}
          borderTop={isDownSM ? `1px solid ${theme.palette.divider}` : "none"}
        >
          <Box
            display="flex"
            alignItems="center"
            flexDirection={isDownSM ? "column" : "row"}
            justifyContent="space-between"
            width="100%"
            gap={4}
            mt={8}
          >
            <Button variant="tertiary" onClick={onClose} fullWidth={isDownSM}>
              {t("navigation.previous")}
            </Button>
            <Button
              data-testid="save-button"
              variant="primary"
              onClick={handleSubmit(onSubmit)}
              fullWidth={isDownSM}
            >
              {t("csvImport.edit")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
